import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowRight, Clock, User, CaretRight, BookOpen } from "@phosphor-icons/react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import ProductCard from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";

const BASE = "https://ebikesandgear.co.uk";

const COLLECTION_FOR = {
  "Sur-Ron": { label: "Sur-Ron Electric Bikes", to: "/collections/surron", params: "category=surron" },
  Specialized: { label: "the Specialized collection", to: "/collections/specialized", params: "search=specialized" },
  "S-Works": { label: "the S-Works collection", to: "/collections/specialized", params: "search=s-works" },
  Trek: { label: "the Trek collection", to: "/collections/trek", params: "search=trek" },
  General: { label: "our full e-bike range", to: "/collections/all", params: "featured=true" },
};

const mdComponents = {
  h1: ({ children }) => <h2 className="mt-10 text-2xl sm:text-3xl font-black tracking-tight">{children}</h2>,
  h2: ({ children }) => <h2 className="mt-10 text-2xl sm:text-3xl font-black tracking-tight">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-8 text-xl font-bold">{children}</h3>,
  h4: ({ children }) => <h4 className="mt-6 text-lg font-bold">{children}</h4>,
  p: ({ children }) => <p className="mt-5 text-lg leading-relaxed text-foreground/90">{children}</p>,
  ul: ({ children }) => <ul className="mt-5 list-disc space-y-2 pl-6 text-lg text-foreground/90">{children}</ul>,
  ol: ({ children }) => <ol className="mt-5 list-decimal space-y-2 pl-6 text-lg text-foreground/90">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-bold text-foreground">{children}</strong>,
  blockquote: ({ children }) => <blockquote className="mt-6 border-l-4 border-primary pl-5 italic text-muted-foreground">{children}</blockquote>,
  hr: () => <hr className="my-10 border-border" />,
  a: ({ href, children }) =>
    href?.startsWith("/") ? (
      <Link to={href} className="font-semibold text-primary underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors">{children}</Link>
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors">{children}</a>
    ),
  table: ({ children }) => <div className="mt-6 overflow-x-auto rounded-2xl border border-border"><table className="w-full min-w-[480px] border-collapse text-sm">{children}</table></div>,
  thead: ({ children }) => <thead className="bg-muted/60 text-foreground">{children}</thead>,
  th: ({ children }) => <th className="px-4 py-3 text-left font-bold">{children}</th>,
  td: ({ children }) => <td className="border-t border-border px-4 py-3 text-foreground/85">{children}</td>,
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [relProducts, setRelProducts] = useState([]);

  useEffect(() => { window.scrollTo(0, 0); api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost(false)); }, [slug]);

  useEffect(() => {
    if (post?.category) {
      const c = COLLECTION_FOR[post.category] || COLLECTION_FOR.General;
      api.get(`/products?${c.params}&limit=3`).then((r) => setRelProducts(r.data.slice(0, 3))).catch(() => setRelProducts([]));
    }
  }, [post?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (post === false) return <div className="container-px py-32 text-center"><h1 className="text-2xl font-black">Article not found</h1><Link to="/blog" className="btn-primary mt-5">Back to blog</Link></div>;
  if (!post) return <div className="container-px py-32 text-center"><div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-muted border-t-primary" /></div>;

  const jsonLd = [{
    "@context": "https://schema.org", "@type": "BlogPosting",
    ...(post.schema || {}),
    headline: post.title,
    image: post.cover?.startsWith("http") ? post.cover : `${BASE}${post.cover}`,
    author: { "@type": "Person", name: post.author },
    publisher: { "@type": "Organization", name: "Ebikes&Gear", url: BASE },
    datePublished: post.created_at, dateModified: post.created_at,
    description: post.meta_description || post.excerpt,
    keywords: (post.tags || []).join(", "),
    articleSection: post.category,
    mainEntityOfPage: { "@type": "WebPage", "@id": `${BASE}/blog/${post.slug}` },
  }, {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${BASE}/` },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${BASE}/blog` },
      { "@type": "ListItem", position: 3, name: post.category, item: `${BASE}/blog` },
      { "@type": "ListItem", position: 4, name: post.title, item: `${BASE}/blog/${post.slug}` },
    ],
  }];

  return (
    <>
      <SEO title={post.title} description={post.meta_description || post.excerpt} path={`/blog/${post.slug}`} image={post.cover} type="article" keywords={(post.tags || []).join(", ")} jsonLd={jsonLd} />

      <article className="container-px py-12">
        <div className="mx-auto max-w-3xl">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground" aria-label="Breadcrumb" data-testid="blog-breadcrumbs">
            <Link to="/" className="hover:text-primary">Home</Link><CaretRight size={12} />
            <Link to="/blog" className="hover:text-primary">Blog</Link><CaretRight size={12} />
            <span className="text-foreground/80">{post.category}</span>
          </nav>
          <span className="overline mt-6 block">{post.category}</span>
          <h1 className="mt-3 text-4xl sm:text-5xl font-black tracking-tighter leading-[1.05] text-balance">{post.title}</h1>
          <div className="mt-5 flex flex-wrap items-center gap-5 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-2"><User size={16} /> {post.author}</span>
            <span className="inline-flex items-center gap-2"><Clock size={16} /> {post.read_time}</span>
            <span>{new Date(post.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</span>
          </div>
        </div>

        <div className="mx-auto mt-8 max-w-4xl overflow-hidden rounded-3xl border border-border">
          <img src={post.cover} alt={post.title} className="aspect-[16/8] w-full object-cover" />
        </div>

        <div className="mx-auto mt-6 max-w-3xl">
          <div data-testid="blog-post-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{post.content}</ReactMarkdown>
          </div>

          <div className="mt-12 flex flex-wrap gap-2">
            {(post.tags || []).map((t) => <span key={t} className="rounded-full bg-muted px-3 py-1 text-xs font-semibold text-muted-foreground">#{t}</span>)}
          </div>

          {(post.internal_links || []).length > 0 && (
            <div className="mt-12 rounded-3xl border border-border bg-card p-7" data-testid="further-reading">
              <h2 className="inline-flex items-center gap-2 text-xl font-black tracking-tight"><BookOpen size={22} className="text-primary" /> Further reading</h2>
              <ul className="mt-4 space-y-3">
                {post.internal_links.map((l) => (
                  <li key={l.slug}>
                    <Link to={`/blog/${l.slug}`} className="group inline-flex items-start gap-2 font-semibold text-foreground/90 hover:text-primary transition-colors">
                      <ArrowRight size={16} className="mt-1 shrink-0 text-primary transition-transform group-hover:translate-x-0.5" /> {l.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-12 rounded-3xl border border-border bg-card p-8" data-testid="related-products">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="overline">Shop the range</p>
                <h2 className="mt-1 text-2xl font-black tracking-tight">Related products</h2>
              </div>
              <Link to={(COLLECTION_FOR[post.category] || COLLECTION_FOR.General).to} className="btn-primary !py-2.5 text-sm" data-testid="related-products-collection-link">
                Shop {(COLLECTION_FOR[post.category] || COLLECTION_FOR.General).label} <ArrowRight size={16} />
              </Link>
            </div>
            {relProducts.length > 0 && (
              <div className="mt-6 grid gap-5 sm:grid-cols-3">
                {relProducts.map((p) => <ProductCard key={p.slug} product={p} />)}
              </div>
            )}
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2" data-testid="post-navigation">
            {post.prev ? (
              <Link to={`/blog/${post.prev.slug}`} className="group rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary" data-testid="post-nav-prev">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">← Previous article</span>
                <p className="mt-1.5 text-sm font-bold leading-snug group-hover:text-primary transition-colors">{post.prev.title}</p>
              </Link>
            ) : <div className="hidden sm:block" />}
            {post.next && (
              <Link to={`/blog/${post.next.slug}`} className="group rounded-2xl border border-border bg-card p-5 text-right transition-colors hover:border-primary" data-testid="post-nav-next">
                <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Next article →</span>
                <p className="mt-1.5 text-sm font-bold leading-snug group-hover:text-primary transition-colors">{post.next.title}</p>
              </Link>
            )}
          </div>
        </div>

        {(post.related || []).length > 0 && (
          <div className="mx-auto mt-16 max-w-4xl" data-testid="related-posts">
            <h2 className="text-2xl font-black tracking-tight">Related posts</h2>
            <div className="mt-6 grid gap-6 md:grid-cols-3">
              {post.related.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.06}>
                  <Link to={`/blog/${p.slug}`} className="group block h-full overflow-hidden rounded-2xl border border-border bg-card" data-testid={`related-post-${p.slug}`}>
                    <img src={p.cover} alt={p.title} loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="p-4">
                      <span className="text-[11px] font-bold uppercase tracking-wider text-primary">{p.category}</span>
                      <h3 className="mt-1 text-sm font-bold leading-tight group-hover:text-primary transition-colors">{p.title}</h3>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        )}
      </article>
    </>
  );
}
